import React, { useState, useEffect } from 'react';
import { DashboardStats, IARIData } from '../types/dashboard';
import { Product } from '../types/products';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { useGlobalFilters } from '../components/GlobalFilterContext';
import { API_BASE } from '../../config';

interface DashboardProps {
  stats: DashboardStats;
  products: Product[];
  highRiskProducts: Product[];
  activityEvents: Array<{id: string; type: string; title: string; detail: string; status: string; timestamp: string | null}>;
  iariData: IARIData | null;
  onSelectProduct: (id: number) => void;
  onNavigate: (path: string) => void;
  AnimatedEagle: React.ComponentType<{ size?: number }>;
  FloatingPill: React.ComponentType<{ delay?: number; x?: number | string; color?: string }>;
  GlobeIcon: React.ComponentType<{ size?: number; animated?: boolean }>;
  StatCard: React.ComponentType<{ title: string; value: string | number; subtitle?: string; color?: string; icon?: React.ReactNode; accent?: string }>;
  IconDatabase: React.ComponentType;
  IconShield: React.ComponentType;
  IconAlert: React.ComponentType;
  IconFlag: React.ComponentType;
  IconChevron: React.ComponentType;
  IconGlobe: React.ComponentType;
}

// ── IARI score color thresholds ──────────────────────────────────────────────
function getIARIColor(score: number): string {
  if (score >= 80) return '#059669';  // strong posture
  if (score >= 60) return '#2563EB';  // moderate
  if (score >= 40) return '#D97706';  // needs attention
  return '#DC2626';                    // critical
}

function getIARILabel(score: number): string {
  if (score >= 80) return 'Strong';
  if (score >= 60) return 'Moderate';
  if (score >= 40) return 'At Risk';
  return 'Critical';
}

export default function DashboardPage({
  stats,
  products,
  highRiskProducts,
  activityEvents,
  iariData,
  onSelectProduct,
  onNavigate,
  AnimatedEagle,
  FloatingPill,
  GlobeIcon,
  StatCard,
  IconDatabase,
  IconShield,
  IconAlert,
  IconFlag,
  IconChevron,
  IconGlobe,
}: DashboardProps) {
  const { T } = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const { filters: globalFilters } = useGlobalFilters();
  const [hoveredCoo, setHoveredCoo] = useState<string | null>(null);

  // Build global filter query string for API calls
  const globalFilterParams = (() => {
    const params = new URLSearchParams();
    if (globalFilters.cooConfidence.length > 0) params.set('coo_confidence', globalFilters.cooConfidence.join(','));
    if (globalFilters.country.length > 0) params.set('country', globalFilters.country.join(','));
    if (globalFilters.riskLevel.length > 0) params.set('risk_level', globalFilters.riskLevel.join(','));
    if (globalFilters.shortageStatus) params.set('shortage_status', globalFilters.shortageStatus);
    return params.toString();
  })();

  // ── Parallel dashboard data fetching ─────────────────────────────────────
  // All three API calls fire simultaneously via Promise.all so tiles
  // appear together instead of staggering one-by-one.
  const [filteredStats, setFilteredStats] = useState<DashboardStats | null>(null);
  const [dashLoading, setDashLoading] = useState(false);

  // China dependency data for dashboard tiles
  const [chinaData, setChinaData] = useState<{
    china_any_pct: number;
    china_any_count: number;
    total_products: number;
    ccp_connected_manufacturer_count: number;
    ccp_connected_product_count: number;
    ccp_connected_product_pct: number;
    ccp_china_api_count: number;
  } | null>(null);

  // API Country of Origin (Comtrade) data
  const [apiCooData, setApiCooData] = useState<{
    total_products: number;
    mapped_count: number;
    not_mapped_count: number;
    china_dep: number;
    india_dep: number;
    us_dep: number;
    low_dep: number;
    china_dep_pct: number;
    india_dep_pct: number;
    us_dep_pct: number;
    low_dep_pct: number;
  } | null>(null);

  useEffect(() => {
    // Clear all data immediately to prevent flash of stale data
    setFilteredStats(null);
    setChinaData(null);
    setApiCooData(null);
    setDashLoading(true);

    let cancelled = false;
    const qs = globalFilterParams ? `?${globalFilterParams}` : '';

    (async () => {
      // Fire all three requests in parallel
      const [statsResult, chinaResult, cooResult] = await Promise.allSettled([
        fetch(`${API_BASE}/metasupply/dashboard/stats${qs}`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : null),
        fetch(`${API_BASE}/metasupply/geopolitical/china-dependency${qs}`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : null),
        fetch(`${API_BASE}/metasupply/dashboard/api-coo${qs}`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : null),
      ]);

      if (cancelled) return;

      // Update all state together so tiles render simultaneously
      const statsData = statsResult.status === 'fulfilled' ? statsResult.value : null;
      const chinaJson = chinaResult.status === 'fulfilled' ? chinaResult.value : null;
      const cooData = cooResult.status === 'fulfilled' ? cooResult.value : null;

      if (statsData) setFilteredStats(statsData);
      if (chinaJson) {
        setChinaData({
          china_any_pct: chinaJson.china_any_pct,
          china_any_count: chinaJson.china_any_count,
          total_products: chinaJson.total_products,
          ccp_connected_manufacturer_count: chinaJson.ccp_connected_manufacturer_count,
          ccp_connected_product_count: chinaJson.ccp_connected_product_count,
          ccp_connected_product_pct: chinaJson.ccp_connected_product_pct,
          ccp_china_api_count: chinaJson.ccp_china_api_count ?? 0,
        });
      }
      if (cooData) setApiCooData(cooData);

      setDashLoading(false);
    })();

    return () => { cancelled = true; };
  }, [globalFilterParams]);

  // Use filtered stats when available, else fall back to props
  const effectiveStats = filteredStats || stats;

  const normalizeCooCountry = (country?: string | null) => {
    const value = (country || '').trim().toLowerCase();
    if (value.includes('india')) return 'India';
    if (value.includes('china') || value.includes('prc') || value.includes("people's republic")) return 'China';
    if (value.includes('united states') || value === 'usa' || value === 'u.s.a.' || value === 'u.s.') return 'United States';
    return 'Other';
  };

  const formatTimeAgo = (iso?: string | null) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const diffH = Math.round((Date.now() - t) / 3600000);
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    return `${Math.round(diffH / 24)}d ago`;
  };

  // Use backend-aggregated api_source_distribution (full universe of products).
  // When global filters are active, the backend already filters the distribution.
  // Falls back to coo_confident_distribution for backwards compat.
  const cooDistribution = (() => {
    const dist = effectiveStats.api_source_distribution ?? effectiveStats.coo_confident_distribution;
    if (dist && Object.keys(dist).length > 0) {
      // Normalize backend keys into the 4 buckets
      let india = 0, china = 0, us = 0, other = 0;
      for (const [country, count] of Object.entries(dist)) {
        const bucket = normalizeCooCountry(country);
        if (bucket === 'India') india += count;
        else if (bucket === 'China') china += count;
        else if (bucket === 'United States') us += count;
        else other += count;
      }
      return { india, china, us, other };
    }
    // Fallback: derive from paginated products if stats not available
    return products.reduce(
      (acc, p) => {
        const bucket = normalizeCooCountry(p.api_source_country);
        if (bucket === 'India') acc.india += 1;
        else if (bucket === 'China') acc.china += 1;
        else if (bucket === 'United States') acc.us += 1;
        else acc.other += 1;
        return acc;
      },
      { india: 0, china: 0, us: 0, other: 0 }
    );
  })();
  const cooTotal = cooDistribution.india + cooDistribution.china + cooDistribution.us + cooDistribution.other;

  // IARI display values
  const iariScore = iariData?.score ?? 0;
  const iariColor = getIARIColor(iariScore);
  const iariLabel = getIARILabel(iariScore);
  const trendDelta = iariData?.trend.delta ?? 0;
  const trendDir = iariData?.trend.direction ?? 'flat';
  const trendArrow = trendDir === 'up' ? '\u25B2' : trendDir === 'down' ? '\u25BC' : '\u2014';
  const trendColor = trendDir === 'up' ? T.success : trendDir === 'down' ? T.danger : T.textLight;

  // SVG ring for the IARI score
  const ringSize = 140;
  const ringStroke = 8;
  const ringR = (ringSize - ringStroke) / 2;
  const ringCirc = 2 * Math.PI * ringR;
  const ringOffset = ringCirc - (iariScore / 100) * ringCirc;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

      {/* Dashboard Stat Tiles — 2 rows of 3 */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: 16 }}>
        {/* Row 1 */}
        <div onClick={() => onNavigate('/products')} style={{ cursor: 'pointer' }}>
          <StatCard title="Total Products" value={effectiveStats.total_products.toLocaleString()} subtitle="Total NDC products tracked in the intelligence database" color={T.navy} accent="#000000" icon={<IconDatabase />} />
        </div>
        {/* Made in America — COO Confidence breakout */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          boxShadow: T.shadow,
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          borderTop: '3px solid #000000',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ color: T.gold }}><IconShield /></span>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase' as const, letterSpacing: '0.1em' }}>Made in America</span>
          </div>
          <div style={{ fontSize: 10, color: T.textLight, marginBottom: 2 }}>Products with US country of origin by confidence level</div>
          {(() => {
            const h = effectiveStats.usa_coo_high_count ?? 0;
            const m = effectiveStats.usa_coo_moderate_count ?? 0;
            const distTotal = h + m;
            return ([{ label: 'HIGH', count: h, color: '#22C55E', key: 'HIGH' }, { label: 'MODERATE', count: m, color: '#F59E0B', key: 'MODERATE' }] as const).map(tier => {
              const pct = distTotal > 0 ? ((tier.count / distTotal) * 100).toFixed(1) : '0.0';
              return (
                <div
                  key={tier.key}
                  onClick={() => onNavigate(`/products?coo=United+States&coo_confidence=${tier.key}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 10px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    background: 'transparent',
                    border: '1.5px solid transparent',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = `${tier.color}18`}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: tier.color,
                    }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>{tier.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500, color: T.textLight }}>
                    {tier.count.toLocaleString()} ({pct}%)
                  </span>
                </div>
              );
            });
          })()}
        </div>
        {/* China-Dependent Products — COO Confidence breakout */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          boxShadow: T.shadow,
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          borderTop: '3px solid #000000',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ color: T.danger }}><IconGlobe /></span>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase' as const, letterSpacing: '0.1em' }}>China-Dependent Products</span>
          </div>
          <div style={{ fontSize: 10, color: T.textLight, marginBottom: 2 }}>Products sourced from China by confidence level</div>
          {(() => {
            const h = effectiveStats.china_coo_high_count ?? 0;
            const m = effectiveStats.china_coo_moderate_count ?? 0;
            const l = effectiveStats.china_coo_low_count ?? 0;
            const distTotal = h + m + l;
            return ([{ label: 'HIGH', count: h, color: '#22C55E', key: 'HIGH' }, { label: 'MODERATE', count: m, color: '#F59E0B', key: 'MODERATE' }, { label: 'LOW', count: l, color: '#EF4444', key: 'LOW' }] as const).map(tier => {
              const pct = distTotal > 0 ? ((tier.count / distTotal) * 100).toFixed(1) : '0.0';
              return (
                <div
                  key={tier.key}
                  onClick={() => onNavigate(`/products?coo=China&coo_confidence=${tier.key}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 10px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    background: 'transparent',
                    border: '1.5px solid transparent',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = `${tier.color}18`}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: tier.color,
                    }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>{tier.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500, color: T.textLight }}>
                    {tier.count.toLocaleString()} ({pct}%)
                  </span>
                </div>
              );
            });
          })()}
        </div>
        {/* Row 2 */}
        <StatCard
          title="CCP-Connected Manufacturers"
          value={chinaData ? `${chinaData.china_any_count > 0 ? ((chinaData.ccp_connected_product_count / chinaData.china_any_count) * 100).toFixed(1) : '0.0'}%` : '--'}
          subtitle={chinaData ? `${chinaData.ccp_connected_manufacturer_count.toLocaleString()} manufacturers, ${chinaData.ccp_connected_product_count.toLocaleString()} products linked to CCP-affiliated entities` : 'Loading...'}
          color="#7C3AED"
          accent="#000000"
          icon={<IconAlert />}
        />
        {/* High Risk — risk level breakout */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          boxShadow: T.shadow,
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          borderTop: '3px solid #000000',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ color: T.danger }}><IconFlag /></span>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase' as const, letterSpacing: '0.1em' }}>Risk Level</span>
          </div>
          <div style={{ fontSize: 10, color: T.textLight, marginBottom: 2 }}>Supply chain risk distribution across all products</div>
          {(() => {
            const h = effectiveStats.high_risk ?? 0;
            const m = effectiveStats.risk_medium ?? 0;
            const l = effectiveStats.risk_low ?? 0;
            const distTotal = h + m + l;
            return ([{ label: 'HIGH', count: h, color: '#EF4444', key: 'high' }, { label: 'MODERATE', count: m, color: '#F59E0B', key: 'medium' }, { label: 'LOW', count: l, color: '#22C55E', key: 'low' }] as const).map(tier => {
              const pct = distTotal > 0 ? ((tier.count / distTotal) * 100).toFixed(1) : '0.0';
              return (
                <div
                  key={tier.key}
                  onClick={() => onNavigate(`/products?risk=${tier.key}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 10px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    background: 'transparent',
                    border: '1.5px solid transparent',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = `${tier.color}18`}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: tier.color,
                    }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>{tier.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500, color: T.textLight }}>
                    {tier.count.toLocaleString()} ({pct}%)
                  </span>
                </div>
              );
            });
          })()}
        </div>
        <div onClick={() => onNavigate('/products?shortage=current')} style={{ cursor: 'pointer', height: '100%' }}>
          <StatCard title="Active Shortages" value={effectiveStats.current_shortages.toLocaleString()} subtitle="Products currently reported in FDA drug shortage database" color={effectiveStats.current_shortages > 0 ? T.warning : T.success} accent="#000000" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16 }}>
        {/* CCP Affiliation tile */}
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow, borderTop: '3px solid #000000' }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>CCP Affiliation</h3>
          <div style={{ fontSize: 10, color: T.textLight, marginBottom: 16 }}>Products and manufacturers linked to CCP-affiliated entities</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div
              onClick={() => onNavigate('/china-dependency')}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 12px", borderRadius: T.radiusSm,
                border: `1px solid ${T.borderLight}`, cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
              onMouseLeave={e => e.currentTarget.style.background = T.white}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>Drugs</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.danger }}>{chinaData ? chinaData.ccp_connected_product_count.toLocaleString() : '—'}</span>
            </div>
            <div
              onClick={() => onNavigate('/apis')}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 12px", borderRadius: T.radiusSm,
                border: `1px solid ${T.borderLight}`, cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
              onMouseLeave={e => e.currentTarget.style.background = T.white}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>API</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.danger }}>{chinaData ? chinaData.ccp_china_api_count.toLocaleString() : '—'}</span>
            </div>
            <div
              onClick={() => onNavigate('/china-dependency')}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 12px", borderRadius: T.radiusSm,
                border: `1px solid ${T.borderLight}`, cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
              onMouseLeave={e => e.currentTarget.style.background = T.white}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>Manufacturers</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#7C3AED" }}>{chinaData ? chinaData.ccp_connected_manufacturer_count.toLocaleString() : '—'}</span>
            </div>
          </div>
        </div>

        {/* Drug Country of Origin — Text rows, no donut */}
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow, borderTop: '3px solid #000000' }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Drug Country of Origin</h3>
          <div style={{ fontSize: 10, color: T.textLight, marginBottom: 16 }}>Finished drug product country of origin distribution</div>
          {(() => {
            const rows = [
              { label: "United States", count: cooDistribution.us, color: T.success },
              { label: "China", count: cooDistribution.china, color: T.danger },
              { label: "India", count: cooDistribution.india, color: T.warning },
              { label: "Other", count: cooDistribution.other, color: "#6366F1" },
            ];
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {rows.map(row => (
                  <div
                    key={row.label}
                    onClick={() => onNavigate(`/products?coo=${encodeURIComponent(row.label)}`)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "10px 12px", borderRadius: T.radiusSm,
                      border: `1px solid ${T.borderLight}`, cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
                    onMouseLeave={e => e.currentTarget.style.background = T.white}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: row.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>{row.label}</span>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: row.color }}>{row.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* API Country of Origin — Comtrade (Option D: Hybrid) */}
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow, borderTop: '3px solid #000000' }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>API Country of Origin</h3>
          <div style={{ fontSize: 10, color: T.textLight, marginBottom: 16 }}>Active ingredient sourcing estimated from aggregate trade flows (Comtrade)</div>
          {apiCooData ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {/* Mapped header */}
              <div
                onClick={() => onNavigate('/apis')}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 12px", borderRadius: T.radiusSm,
                  background: T.offWhite, border: `1px solid ${T.border}`, cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                onMouseLeave={e => e.currentTarget.style.background = T.offWhite}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Mapped</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.navy }}>{apiCooData.mapped_count.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 500, color: T.textLight }}>of {apiCooData.total_products.toLocaleString()}</span></span>
              </div>
              {/* Indented dependency breakdown */}
              {[
                { label: 'China dep.', count: apiCooData.china_dep, pct: apiCooData.china_dep_pct, color: T.danger, expand: 'china' },
                { label: 'India dep.', count: apiCooData.india_dep, pct: apiCooData.india_dep_pct, color: T.warning, expand: 'india' },
                { label: 'USA dep.', count: apiCooData.us_dep, pct: apiCooData.us_dep_pct, color: '#2563EB', expand: 'us' },
                { label: 'Other dep.', count: apiCooData.low_dep, pct: apiCooData.low_dep_pct, color: T.success, expand: 'other' },
              ].map(row => (
                <div
                  key={row.label}
                  onClick={() => onNavigate(`/apis?expand=${row.expand}`)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "8px 12px 8px 28px", borderRadius: T.radiusSm,
                    border: `1px solid ${T.borderLight}`, cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
                  onMouseLeave={e => e.currentTarget.style.background = T.white}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: T.textLight, fontSize: 11 }}>└</span>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: row.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.textDark }}>{row.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: row.color }}>{row.count.toLocaleString()} <span style={{ fontSize: 10, fontWeight: 500, color: T.textLight }}>({row.pct}%)</span></span>
                </div>
              ))}
              {/* Not Mapped */}
              <div
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 12px", borderRadius: T.radiusSm,
                  border: `1px solid ${T.borderLight}`,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600, color: T.textLight }}>Not Mapped</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.textLight }}>{apiCooData.not_mapped_count.toLocaleString()}</span>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {['Mapped', 'Not Mapped'].map(label => (
                <div key={label} style={{ padding: "10px 12px", borderRadius: T.radiusSm, border: `1px solid ${T.borderLight}`, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.textLight }}>{label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: T.textLight }}>—</span>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
